<template>
  <block-item-card
    v-if="data && data.result && data.result.historicalEveChanges"
    :min-height="minBlockHeight"
    :title="curveTitle"
  >
    <v-row
      class="ma-0 pt-0 pb-4 px-2"
      no-gutters
    >
      <v-col
        cols="12"
        class="rounded-lg"
      >
        <v-lazy>
          <div class="chart-fixed-wrap">
            <e-chart
              :key="`echart-sofr-rate`"
              :ref="`echart-sofr-rate`"
              :autoresize="true"
              :option="graphData"
              :style="`height: 350px;`"
              class="chart"
            />
          </div>
        </v-lazy>
      </v-col>
    </v-row>
  </block-item-card>
</template>

  <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { formatDateShort }                         from '@/lib/utils/helper'
import { use }                 from 'echarts/core'
import { LineChart, ScatterChart, CustomChart } from 'echarts/charts'

import {
  GridComponent, TooltipComponent, LegendComponent,
  MarkPointComponent, MarkLineComponent, GraphicComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([
  LineChart,
  GridComponent, TooltipComponent, LegendComponent,
  MarkPointComponent, MarkLineComponent, GraphicComponent,
  CanvasRenderer,
  ScatterChart,
  CustomChart
])

export default {
  name      : 'SoftRate',
  components: {
    BlockItemCard
  },
  mixins: [chartsColorMixin],
  props : {
    data: {
      type   : Object,
      default: () => null
    }
  },
  data () {
    return {
      minBlockHeight: 350
    }
  },
  computed: {
    historicalEveChanges () {
      return this.data?.result?.historicalEveChanges || null
    },
    dates () {
      return this.historicalEveChanges?.dates?.map(date => formatDateShort(date, false, 0)) || []
    },
    rates () {
      return this.historicalEveChanges?.rates || []
    },

    baseCurrency () {
      return this.data?.baseCurrency || null
    },

    curveTitle () {
      return this.historicalEveChanges?.curveTitle || ''
    },

    graphData  () {
      if (!this.rates) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            const value = params[0].value + '%'
            return `
                  <div style="margin: 2px 0;">
                      <div style="font-size:12px;">${params[0].axisValueLabel}</div>
                      <div style="font-weight:700;">${value}</div>
                  </div>`
          }
        },
        legend: { show: false },
        grid  : {
          margin      : 0,
          left        : 60,
          top         : 50,
          right       : 30,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type    : 'category',
          data    : this.dates,
          axisTick: {
            show: false
          },
          axisLabel: {
            margin      : 10,
            show        : true,
            color       : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize    : 12,
            showMaxLabel: true,
            interval    : 0,
            formatter   : (value, idx) => {
              const lastMonth = this.dates[this.dates.length - 1].split(' ')[0]
              const lastYear = +this.dates[this.dates.length - 1].split(' ')[1]
              const isLastYearOdd = lastYear % 2 !== 0
              const lastIndex = this.dates.length - 1

              // If not the last index and month doesn't match, hide it
              if (idx !== lastIndex && value.split(' ')[0] !== lastMonth) {
                return ''
              }

              // If this is a matching month, check if year matches odd/even pattern
              const currentYear = +value.split(' ')[1]
              const isCurrentYearOdd = currentYear % 2 !== 0

              // Show only if year parity matches the last year's parity, or if it's the last index
              if (idx === lastIndex || isCurrentYearOdd === isLastYearOdd) {
                return value
              }

              return ''
            }
          },
          boundaryGap: true,
          scale      : false,
          axisLine   : {
            show: false
          }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -50,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              if (!value) return '0%'
              return value + '%'
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'SOFR Rate',
          type     : 'line',
          data     : this.rates,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-orange-01')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 0,
          areaStyle : {
            color: {
              type      : 'linear',
              x         : 0,
              y         : 0,
              x2        : 0,
              y2        : 1,
              colorStops: [
                {
                  offset: 0,
                  color : this.themeColorRGBA('synthesis-ui-orange-05', 1)
                },
                {
                  offset: 1,
                  color : 'white'
                }
              ]
            }
          }
        }]
      }
    }

  },

  watch: { },
  mounted () {},
  created () {
  },
  methods: {
  }
}
        </script>

        <style scoped>

      .chart-fixed-wrap {
        height: 350px;
        position: relative;
        overflow: hidden;
      }

      .chart {
        height: 100%;
        width: 100%;
        display: block;
      }
        </style>
